import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mall-my-list"
}
const _hoisted_2 = { class: "mall-my-list__head is-split" }
const _hoisted_3 = {
  key: 0,
  class: "mall-my-list__empty"
}
const _hoisted_4 = {
  key: 1,
  class: "mall-my-list__empty"
}
const _hoisted_5 = {
  key: 2,
  class: "mall-my-list__empty"
}
const _hoisted_6 = {
  key: 3,
  class: "mall-my-list__cards"
}
const _hoisted_7 = { class: "mall-my-list__meta" }
const _hoisted_8 = {
  key: 0,
  class: "mall-my-list__sub"
}
const _hoisted_9 = {
  key: 1,
  class: "portal-page board-page consult-board-list"
}
const _hoisted_10 = { class: "portal-page__inner" }
const _hoisted_11 = { class: "portal-page__head consult-board-list__head" }
const _hoisted_12 = { class: "board-page__table portal-panel" }
const _hoisted_13 = { class: "board-table" }
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { key: 1 }
const _hoisted_16 = {
  colspan: "4",
  class: "board-table__status"
}
const _hoisted_17 = { key: 2 }
const _hoisted_18 = {
  colspan: "4",
  class: "board-table__status"
}
const _hoisted_19 = { class: "consult-board-list__empty" }
const _hoisted_20 = { class: "board-table__num" }
const _hoisted_21 = {
  key: 0,
  class: "consult-board-list__sub"
}
const _hoisted_22 = { class: "board-table__cat" }
const _hoisted_23 = { class: "board-table__date" }

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import PortalPager from '@/components/common/PortalPager.vue'
import { fetchMyConsultPosts } from '@/api/consultBoard'
import { fetchMyQnas, type MallQna } from '@/api/mallReview'
import { mallProductPath } from '@/constants/mall'
import { MALL_MY_CONSULT_WRITE_PATH, MALL_MY_CONSULTS_PATH } from '@/constants/mallMy'
import type { MyConsultListItem } from '@/types/consultBoard'

interface InquiryRow {
  key: string
  num: number
  title: string
  sub: string
  status: 'WAIT' | 'DONE'
  createdAt: string
  date: string
  to: string
}

const pageSize = 10


export default /*@__PURE__*/_defineComponent({
  __name: 'ConsultBoardListPage',
  setup(__props) {

const route = useRoute()
const isMall = computed(() => route.meta.layout === 'mall')
const listPath = computed(() => (isMall.value ? MALL_MY_CONSULTS_PATH : '/consult-board'))
const writePath = computed(() => (isMall.value ? MALL_MY_CONSULT_WRITE_PATH : '/consult-board/write'))
const items = ref<MyConsultListItem[]>([])
const productQnas = ref<MallQna[]>([])
const loading = ref(true)
const loadError = ref('')
const page = ref(1)
const totalPages = ref(1)
const total = ref(0)
const mallRows = computed(() => {
  const consultRows = items.value.map((item) => ({
    key: `consult-${item.id}`,
    title: item.title,
    sub: '',
    status: item.status,
    createdAt: item.createdAt,
    to: `${listPath.value}/${item.id}`,
  }))
  const qnaRows = productQnas.value.map((item) => ({
    key: `qna-${item.idx}`,
    title: item.question,
    sub: item.productName,
    status: (item.answer ? 'DONE' : 'WAIT') as 'WAIT' | 'DONE',
    createdAt: item.date,
    to: `${mallProductPath(item.productCode)}?tab=qna`,
  }))
  return [...consultRows, ...qnaRows].sort(
    (a, b) => rowTime(b.createdAt) - rowTime(a.createdAt),
  )
})

const displayRows = computed<InquiryRow[]>(() => {
  if (!isMall.value) {
    return items.value.map((item, index) => ({
      key: `consult-${item.id}`,
      num: total.value - (page.value - 1) * pageSize - index,
      title: item.title,
      sub: '',
      status: item.status,
      createdAt: item.createdAt,
      date: formatDate(item.createdAt),
      to: `${listPath.value}/${item.id}`,
    }))
  }
  const start = (page.value - 1) * pageSize
  return mallRows.value.slice(start, start + pageSize).map((row, index) => ({
    ...row,
    num: total.value - start - index,
    date: formatDate(row.createdAt),
  }))
})

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    if (isMall.value) {
      const [consults, qnas] = await Promise.all([
        fetchMyConsultPosts({ page: 1, limit: 100, channel: 'MALL' }),
        fetchMyQnas().catch(() => [] as MallQna[]),
      ])
      items.value = consults.items
      productQnas.value = qnas
      total.value = mallRows.value.length
      totalPages.value = Math.max(1, Math.ceil(total.value / pageSize))
      return
    }
    const data = await fetchMyConsultPosts({
      page: page.value,
      limit: pageSize,
      channel: 'PORTAL',
    })
    items.value = data.items
    total.value = data.pagination.total
    totalPages.value = data.pagination.totalPages
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : '목록을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function rowTime(value: string) {
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? 0 : time
}

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
}

watch(page, () => {
  if (!isMall.value) void load()
})

onMounted(() => {
  void load()
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (isMall.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("header", _hoisted_2, [
          _cache[3] || (_cache[3] = _createElementVNode("div", null, [
            _createElementVNode("h1", null, "문의 내역"),
            _createElementVNode("p", null, "상품 문의와 그 밖 문의를 여기서 작성하고, 남긴 글과 답변을 확인합니다.")
          ], -1)),
          _createVNode(_component_router_link, {
            to: writePath.value,
            class: "mall-my-list__primary"
          }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createTextVNode("문의하기", -1)
            ]))]),
            _: 1
          }, 8, ["to"])
        ]),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, "불러오는 중입니다…"))
          : (loadError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("p", null, _toDisplayString(loadError.value), 1)
              ]))
            : (!displayRows.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _cache[5] || (_cache[5] = _createElementVNode("p", null, "아직 작성한 문의가 없습니다.", -1)),
                  _createVNode(_component_router_link, { to: writePath.value }, {
                    default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                      _createTextVNode("문의하기", -1)
                    ]))]),
                    _: 1
                  }, 8, ["to"])
                ]))
              : (_openBlock(), _createElementBlock("ul", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayRows.value, (row) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: row.key
                    }, [
                      _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("strong", null, _toDisplayString(row.status === 'DONE' ? '답변완료' : '답변대기'), 1),
                        _createElementVNode("time", null, _toDisplayString(row.date), 1)
                      ]),
                      _createVNode(_component_router_link, {
                        to: row.to,
                        class: "mall-my-list__title-link"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(row.title), 1)
                        ]),
                        _: 2
                      }, 1032, ["to"]),
                      (row.sub)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(row.sub), 1))
                        : _createCommentVNode("", true)
                    ]))
                  }), 128))
                ])),
        _createVNode(PortalPager, {
          modelValue: page.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((page).value = $event)),
          "total-pages": totalPages.value
        }, null, 8, ["modelValue", "total-pages"])
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_9, [
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("header", _hoisted_11, [
            _cache[7] || (_cache[7] = _createElementVNode("div", null, [
              _createElementVNode("p", { class: "portal-page__eyebrow" }, "상담"),
              _createElementVNode("h1", { class: "portal-page__title" }, "상담 신청 내역"),
              _createElementVNode("p", { class: "portal-page__desc" }, "장기요양기관 운영 상담과 답변을 확인합니다.")
            ], -1)),
            _createVNode(_component_router_link, {
              to: writePath.value,
              class: "portal-btn portal-btn--primary consult-board-list__write"
            }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode(" 상담 신청 ", -1)
              ]))]),
              _: 1
            }, 8, ["to"])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("table", _hoisted_13, [
              _cache[11] || (_cache[11] = _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", {
                    scope: "col",
                    class: "board-table__num"
                  }, "번호"),
                  _createElementVNode("th", { scope: "col" }, "제목"),
                  _createElementVNode("th", {
                    scope: "col",
                    class: "board-table__cat"
                  }, "상태"),
                  _createElementVNode("th", {
                    scope: "col",
                    class: "board-table__date"
                  }, "작성일")
                ])
              ], -1)),
              _createElementVNode("tbody", null, [
                (loading.value)
                  ? (_openBlock(), _createElementBlock("tr", _hoisted_14, [...(_cache[8] || (_cache[8] = [
                      _createElementVNode("td", {
                        colspan: "4",
                        class: "board-table__status"
                      }, "불러오는 중…", -1)
                    ]))]))
                  : (loadError.value)
                    ? (_openBlock(), _createElementBlock("tr", _hoisted_15, [
                        _createElementVNode("td", _hoisted_16, _toDisplayString(loadError.value), 1)
                      ]))
                    : (!displayRows.value.length)
                      ? (_openBlock(), _createElementBlock("tr", _hoisted_17, [
                          _createElementVNode("td", _hoisted_18, [
                            _createElementVNode("div", _hoisted_19, [
                              _cache[10] || (_cache[10] = _createElementVNode("p", null, "아직 신청한 상담이 없습니다.", -1)),
                              _createVNode(_component_router_link, {
                                to: writePath.value,
                                class: "portal-btn portal-btn--primary"
                              }, {
                                default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                  _createTextVNode(" 상담 신청하기 ", -1)
                                ]))]),
                                _: 1
                              }, 8, ["to"])
                            ])
                          ])
                        ]))
                      : (_openBlock(true), _createElementBlock(_Fragment, { key: 3 }, _renderList(displayRows.value, (row) => {
                          return (_openBlock(), _createElementBlock("tr", {
                            key: row.key
                          }, [
                            _createElementVNode("td", _hoisted_20, _toDisplayString(row.num), 1),
                            _createElementVNode("td", null, [
                              _createVNode(_component_router_link, {
                                to: row.to,
                                class: "board-table__title"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(row.title), 1)
                                ]),
                                _: 2
                              }, 1032, ["to"]),
                              (row.sub)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(row.sub), 1))
                                : _createCommentVNode("", true)
                            ]),
                            _createElementVNode("td", _hoisted_22, [
                              _createElementVNode("span", {
                                class: _normalizeClass(["consult-board-list__status", row.status === 'DONE' ? 'is-done' : 'is-wait'])
                              }, _toDisplayString(row.status === 'DONE' ? '답변완료' : '답변대기'), 3)
                            ]),
                            _createElementVNode("td", _hoisted_23, _toDisplayString(row.date), 1)
                          ]))
                        }), 128))
              ])
            ])
          ]),
          _createVNode(PortalPager, {
            modelValue: page.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((page).value = $event)),
            "total-pages": totalPages.value
          }, null, 8, ["modelValue", "total-pages"])
        ])
      ]))
}
}

})